/**
 * Configuration — Fraud & Security
 * Multiple Fraud & Security Rules per Source are allowed. Current Rules use No Conditions.
 */

export const FRAUD_SECURITY_ROUTE = '/admin/configuration/fraud-security';

export const FRAUD_REQUEST_FORM_DISPLAY = 'FORM-000041 — Fraud & Security Information Request Form';
export const FRAUD_RESPONSE_FORM_DISPLAY = 'FORM-000042 — Fraud & Security Information Response Form';
export const FRAUD_DECISION_FORM_DISPLAY = 'FORM-000043 — Fraud & Security Decision Form';

export const RESPONSE_PERIOD = '14 Calendar Days';
export const PROTECTION_ACTION = 'Sign Out Active Sessions';

export const SOURCE_OPTIONS = ['Business', 'Customer', 'Billing', 'System'];
export const PRIORITY_OPTIONS = ['Low', 'Medium', 'High'];
export const SEVERITY_HINT_OPTIONS = ['No Suggestion', 'Low', 'Medium', 'High'];
export const MODE_OPTIONS = ['Unavailable', 'Optional', 'Required'];
export const DURATION_OPTIONS = [
  '24 Hours',
  '3 Calendar Days',
  '7 Calendar Days',
  '14 Calendar Days',
  '30 Calendar Days',
];

export const SOURCE_FILTER_OPTIONS = [
  { value: 'All', label: 'All Sources' },
  ...SOURCE_OPTIONS.map((value) => ({ value, label: value })),
];

export const PRIORITY_FILTER_OPTIONS = [
  { value: 'All', label: 'All Priorities' },
  ...PRIORITY_OPTIONS.map((value) => ({ value, label: value })),
];

const SAMPLE_UPDATED = '2026-09-13T09:00:00+10:00';

export const MESSAGES = {
  noSavedRules: 'No Fraud & Security Rules have been added.',
  noMatchingResults: 'No Fraud & Security Rules match the selected search or filters.',
  accessDenied: 'You do not have permission to view Fraud & Security.',
};

export const VALIDATION_MESSAGES = {
  ruleName: 'Enter a Rule Name.',
  duplicateName: 'This Rule Name is already used.',
  source: 'Select a valid Source.',
  sourceChanged: 'Source cannot be changed after Save.',
  ruleIdChanged: 'Rule ID cannot be changed.',
  priority: 'Select a valid Priority.',
  severityHint: 'Select a valid Severity Hint.',
  mode: 'Select a valid Mode.',
  duration: 'Select a Duration.',
  durationUnsupported: 'Select an available Duration.',
  durationInapplicable: 'Remove the inapplicable Duration.',
  protectionAction: 'Select Sign Out Active Sessions.',
  protectionUnsupported: 'Select an available Protection Action.',
  protectionInapplicable: 'Remove the inapplicable action selection.',
  permanentBan: 'Permanent Ban must remain selected.',
  enforcement: 'Select an available Enforcement choice.',
  responsePeriod: 'Response Period must be 14 Calendar Days.',
  conditions: 'Conditions are not available.',
  unavailable: 'Review the unavailable selection.',
  ruleChanged: 'This Rule has changed. Refresh before saving.',
  checksFailed: 'Checks could not be completed. Try again.',
};

export function trimText(value) {
  return String(value ?? '').trim();
}

export function normalizeRuleName(value) {
  return trimText(value).replace(/\s+/g, ' ').toLowerCase();
}

export function formatFraudSecurityDate(value) {
  if (!value || !String(value).trim() || value === '—') return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatFraudSecurityDateTime(value) {
  if (!value || !String(value).trim() || value === '—') return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function responsePeriodFor(mode) {
  return mode === 'Optional' || mode === 'Required' ? RESPONSE_PERIOD : '—';
}

export function formatRestrictionSummary(mode, duration) {
  if (!mode) return '—';
  if (mode === 'Unavailable') return 'Unavailable';
  if (duration === '24 Hours') return `${mode} · 24 Hours`;
  const days = String(duration || '').match(/^(\d+)\s+Calendar Days$/);
  if (days) return `${mode} · ${days[1]} days`;
  return duration ? `${mode} · ${duration}` : mode;
}

export function formatEnforcementSummary(rule) {
  if (!rule) return '—';
  return rule.finalWarning ? 'Warning + Ban' : 'Ban only';
}

export function formatEnforcementDetail(rule) {
  if (!rule) return '—';
  return rule.finalWarning ? 'Final Warning and Permanent Ban' : 'Permanent Ban';
}

export function createEmptyFraudSecurityForm() {
  return {
    id: '',
    ruleName: '',
    source: '',
    priority: '',
    severityHint: '',
    infoRequest: '',
    restriction: '',
    duration: '',
    protection: '',
    signOutSessions: false,
    permanentBan: true,
    finalWarning: false,
    conditions: [],
    createdOn: '',
    addedBy: '',
    lastUpdatedOn: '',
    updatedBy: '',
  };
}

const sampleRule = (row) => ({
  ...createEmptyFraudSecurityForm(),
  ...row,
  permanentBan: true,
  conditions: [],
  createdOn: SAMPLE_UPDATED,
  lastUpdatedOn: SAMPLE_UPDATED,
  addedBy: '—',
  updatedBy: '—',
});

export function buildSampleFraudSecurityRules() {
  return [
    sampleRule({
      id: 'FSR-0001',
      ruleName: 'Business Review',
      source: 'Business',
      priority: 'High',
      severityHint: 'High',
      infoRequest: 'Required',
      restriction: 'Required',
      duration: '7 Calendar Days',
      protection: 'Required',
      signOutSessions: true,
      finalWarning: true,
    }),
    sampleRule({
      id: 'FSR-0002',
      ruleName: 'Customer Review',
      source: 'Customer',
      priority: 'Medium',
      severityHint: 'Medium',
      infoRequest: 'Optional',
      restriction: 'Optional',
      duration: '3 Calendar Days',
      protection: 'Optional',
      signOutSessions: true,
      finalWarning: true,
    }),
    sampleRule({
      id: 'FSR-0003',
      ruleName: 'Billing Review',
      source: 'Billing',
      priority: 'High',
      severityHint: 'High',
      infoRequest: 'Required',
      restriction: 'Optional',
      duration: '7 Calendar Days',
      protection: 'Required',
      signOutSessions: true,
      finalWarning: false,
    }),
    sampleRule({
      id: 'FSR-0004',
      ruleName: 'System Review',
      source: 'System',
      priority: 'Medium',
      severityHint: 'No Suggestion',
      infoRequest: 'Unavailable',
      restriction: 'Unavailable',
      duration: '',
      protection: 'Optional',
      signOutSessions: true,
      finalWarning: true,
    }),
  ];
}

export function sortFraudSecurityRules(rules = []) {
  return [...rules].sort((a, b) => {
    const ta = new Date(a.lastUpdatedOn || 0).getTime();
    const tb = new Date(b.lastUpdatedOn || 0).getTime();
    if (tb !== ta) return tb - ta;
    return String(a.id || '').localeCompare(String(b.id || ''));
  });
}

export function availableSources() {
  return [...SOURCE_OPTIONS];
}

export function nextFraudSecurityRuleId(rules = []) {
  const nums = (rules || []).map((rule) => Number(String(rule.id || '').replace(/\D/g, '')) || 0);
  return `FSR-${String(Math.max(0, ...nums) + 1).padStart(4, '0')}`;
}

export function ruleFingerprint(rule = {}) {
  const restrictionApplies = rule.restriction === 'Optional' || rule.restriction === 'Required';
  const protectionApplies = rule.protection === 'Optional' || rule.protection === 'Required';
  return JSON.stringify({
    id: rule.id || '',
    ruleName: normalizeRuleName(rule.ruleName),
    source: rule.source || '',
    priority: rule.priority || '',
    severityHint: rule.severityHint || '',
    infoRequest: rule.infoRequest || '',
    restriction: rule.restriction || '',
    duration: restrictionApplies ? rule.duration || '' : '',
    protection: rule.protection || '',
    signOutSessions: protectionApplies ? Boolean(rule.signOutSessions) : false,
    finalWarning: Boolean(rule.finalWarning),
    permanentBan: rule.permanentBan !== false,
  });
}

export function normalizeFraudSecurityForm(form = {}) {
  const next = {
    ...createEmptyFraudSecurityForm(),
    ...form,
    ruleName: form.ruleName || '',
    permanentBan: true,
    conditions: Array.isArray(form.conditions) ? form.conditions : [],
  };
  if (next.restriction === 'Unavailable') next.duration = '';
  if (next.protection === 'Unavailable') next.signOutSessions = false;
  return next;
}

const pushError = (errors, key, message) => {
  if (!errors[key]) errors[key] = message;
};

export function collectFraudSecuritySaveErrors(form, rules = [], { isCreate = false, openedRuleFingerprint = '' } = {}) {
  const errors = {};
  const name = trimText(form?.ruleName);
  const existing = (rules || []).find((rule) => rule.id && rule.id === form?.id);

  if (!isCreate) {
    if (!existing) {
      pushError(errors, 'checksFailed', VALIDATION_MESSAGES.checksFailed);
      return errors;
    }
    if (openedRuleFingerprint && ruleFingerprint(existing) !== openedRuleFingerprint) {
      pushError(errors, 'ruleChanged', VALIDATION_MESSAGES.ruleChanged);
    }
    if (form?.id && form.id !== existing.id) {
      pushError(errors, 'ruleId', VALIDATION_MESSAGES.ruleIdChanged);
    }
    if (form?.source && form.source !== existing.source) {
      pushError(errors, 'source', VALIDATION_MESSAGES.sourceChanged);
    }
  }

  if (!name) pushError(errors, 'ruleName', VALIDATION_MESSAGES.ruleName);
  else if (
    (rules || []).some(
      (rule) => rule.id !== form?.id && normalizeRuleName(rule.ruleName) === normalizeRuleName(name)
    )
  ) {
    pushError(errors, 'ruleName', VALIDATION_MESSAGES.duplicateName);
  }

  if (!SOURCE_OPTIONS.includes(form?.source)) {
    pushError(errors, 'source', VALIDATION_MESSAGES.source);
  }

  if (!PRIORITY_OPTIONS.includes(form?.priority)) {
    pushError(errors, 'priority', VALIDATION_MESSAGES.priority);
  }
  if (!SEVERITY_HINT_OPTIONS.includes(form?.severityHint)) {
    pushError(errors, 'severityHint', VALIDATION_MESSAGES.severityHint);
  }

  ['infoRequest', 'restriction', 'protection'].forEach((field) => {
    if (!MODE_OPTIONS.includes(form?.[field])) pushError(errors, field, VALIDATION_MESSAGES.mode);
  });

  const restrictionApplies = form?.restriction === 'Optional' || form?.restriction === 'Required';
  if (form?.restriction === 'Unavailable' && trimText(form?.duration)) {
    pushError(errors, 'duration', VALIDATION_MESSAGES.durationInapplicable);
  } else if (restrictionApplies && !trimText(form?.duration)) {
    pushError(errors, 'duration', VALIDATION_MESSAGES.duration);
  } else if (restrictionApplies && !DURATION_OPTIONS.includes(form?.duration)) {
    pushError(errors, 'duration', VALIDATION_MESSAGES.durationUnsupported);
  }

  const protectionApplies = form?.protection === 'Optional' || form?.protection === 'Required';
  if (form?.protection === 'Unavailable' && form?.signOutSessions) {
    pushError(errors, 'signOutSessions', VALIDATION_MESSAGES.protectionInapplicable);
  } else if (protectionApplies && !form?.signOutSessions) {
    pushError(errors, 'signOutSessions', VALIDATION_MESSAGES.protectionAction);
  }

  if (form?.permanentBan === false) {
    pushError(errors, 'permanentBan', VALIDATION_MESSAGES.permanentBan);
  }

  if (form?.responsePeriod && form.responsePeriod !== '—' && form.responsePeriod !== RESPONSE_PERIOD) {
    pushError(errors, 'responsePeriod', VALIDATION_MESSAGES.responsePeriod);
  }

  if (Array.isArray(form?.conditions) && form.conditions.length > 0) {
    pushError(errors, 'conditions', VALIDATION_MESSAGES.conditions);
  }

  return errors;
}
